import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';

const MODAL_CONFIG = {
  bounced: {
    title: 'Bounced Contacts',
    description: 'Contacts with failed email deliveries',
    endpoint: '/api/contact-management/bounced-contacts',
    allowDelete: true,
    emptyMessage: 'No bounced contacts found'
  },
  not_interested: {
    title: 'Not Interested Contacts',
    description: 'Contacts requesting removal from campaigns',
    endpoint: '/api/contact-management/not-interested',
    allowDelete: true,
    emptyMessage: 'No not interested responses found'
  },
  out_of_office: {
    title: 'Out of Office Responses',
    description: 'Contacts who are temporarily unavailable',
    endpoint: '/api/contact-management/out-of-office',
    allowDelete: false,
    emptyMessage: 'No out of office responses found'
  },
  forwarded: {
    title: 'Forwarded Contacts',
    description: 'Responses forwarding to new stakeholders',
    endpoint: '/api/contact-management/forwarded-contacts',
    allowDelete: false,
    emptyMessage: 'No forwarded contacts found'
  }
};

const normalizeContactType = (type = '') => {
  switch (type) {
    case 'cold_leads':
    case 'cold':
      return 'cold';
    case 'warm_prospects':
    case 'warm':
      return 'warm';
    case 'active_clients':
    case 'active':
      return 'active';
    default:
      return 'unknown';
  }
};

const contactTypeMeta = {
  cold: { label: 'Cold Lead', className: 'bg-blue-100 text-blue-700' },
  warm: { label: 'Warm Prospect', className: 'bg-orange-100 text-orange-700' },
  active: { label: 'Active Client', className: 'bg-green-100 text-green-700' },
  unknown: { label: 'Unknown', className: 'bg-gray-100 text-gray-600' }
};

const emailStatusMeta = {
  Scraping_Failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
  Scraping_Bounce: { label: 'Bounced', className: 'bg-yellow-100 text-yellow-700' },
  Empty_Email: { label: 'Empty Email', className: 'bg-gray-100 text-gray-700' }
};

const getEmailStatusMeta = (status) =>
  emailStatusMeta[status] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-700' };

const formatResponseStatus = (value) => {
  if (!value) return 'Unknown';
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString();
  } catch (err) {
    return value;
  }
};

const BouncedContactsModal = ({ isOpen, type = 'bounced', onClose }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'contact_type', direction: 'asc' });

  const config = useMemo(() => MODAL_CONFIG[type] || MODAL_CONFIG.bounced, [type]);
  const isBouncedView = type === 'bounced';

  const loadContacts = useCallback(async () => {
    if (!isOpen) return;
    try {
      setLoading(true);
      const response = await axios.get(config.endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        setContacts(response.data.contacts || []);
      }
    } catch (error) {
      console.error(`Failed to load ${type} contacts:`, error);
      alert('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [config.endpoint, isOpen, type]);

  useEffect(() => {
    if (isOpen) {
      loadContacts();
    } else {
      setContacts([]);
    }
  }, [isOpen, loadContacts]);

  useEffect(() => {
    setSortConfig({ key: isBouncedView ? 'contact_type' : 'response_date', direction: 'asc' });
  }, [isBouncedView]);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });

    setContacts((prev) => {
      const sorted = [...prev];
      sorted.sort((a, b) => {
        if (key === 'response_date' || key === 'created_at') {
          const aValue = a[key] ? new Date(a[key]).getTime() : 0;
          const bValue = b[key] ? new Date(b[key]).getTime() : 0;
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (key === 'contact_type') {
          const aLabel = contactTypeMeta[normalizeContactType(a.contact_type)].label;
          const bLabel = contactTypeMeta[normalizeContactType(b.contact_type)].label;
          return direction === 'asc'
            ? aLabel.localeCompare(bLabel)
            : bLabel.localeCompare(aLabel);
        }
        const aValue = (a[key] || '').toString().toLowerCase();
        const bValue = (b[key] || '').toString().toLowerCase();
        return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      });
      return sorted;
    });
  };

  const handleDeleteContact = async (contact) => {
    if (!config.allowDelete) return;

    const confirmMessage =
      contact.source === 'zoho'
        ? 'Are you sure you want to delete this contact from Zoho?'
        : 'Are you sure you want to delete this contact?';

    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);
      const response = await axios.delete(`/api/contact-management/contacts/${contact.id}`, {
        params: {
          contactType: contact.contact_type,
          source: contact.source,
          zoho_id: contact.zoho_id
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        alert('Contact deleted successfully!');
        loadContacts();
      }
    } catch (error) {
      console.error('Failed to delete contact:', error);
      alert('Failed to delete contact: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setContacts([]);
    setSortConfig({ key: 'contact_type', direction: 'asc' });
    onClose?.();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold">
              {config.title} ({contacts.length})
            </h2>
            <p className="text-sm text-gray-500">{config.description}</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-900">{config.description}</h3>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
              <p>Loading contacts...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">{config.emptyMessage}</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  {isBouncedView ? (
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-900">Contact Info</th>
                      <th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('contact_type')}
                      >
                        <div className="flex items-center gap-1">
                          Contact Type
                          {sortConfig.key === 'contact_type' &&
                            (sortConfig.direction === 'asc' ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            ))}
                        </div>
                      </th>
                      <th className="text-left p-3 font-medium text-gray-900">Email Status</th>
                      <th className="text-left p-3 font-medium text-gray-900">Source</th>
                      <th className="text-left p-3 font-medium text-gray-900">Created</th>
                      {config.allowDelete && (
                        <th className="text-left p-3 font-medium text-gray-900">Actions</th>
                      )}
                    </tr>
                  ) : (
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-900">Contact Info</th>
                      <th className="text-left p-3 font-medium text-gray-900">Response Details</th>
                      <th className="text-left p-3 font-medium text-gray-900">Campaign</th>
                      <th className="text-left p-3 font-medium text-gray-900">Received</th>
                      {config.allowDelete && (
                        <th className="text-left p-3 font-medium text-gray-900">Actions</th>
                      )}
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contacts.map((contact, index) => {
                    const normalized = normalizeContactType(contact.contact_type);
                    const typeMeta = contactTypeMeta[normalized];
                    const emailMeta = getEmailStatusMeta(contact.email_status);
                    const rowKey = contact.id || contact.recipient_id || index;

                    return (
                      <tr key={`${rowKey}-${index}`} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {contact.first_name} {contact.last_name}
                            </div>
                            <div className="text-xs text-gray-500">{contact.job_title}</div>
                            <div className="text-xs text-gray-400">{contact.email}</div>
                            {contact.company && <div className="text-xs text-gray-500">{contact.company}</div>}
                          </div>
                        </td>
                        {isBouncedView ? (
                          <>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeMeta.className}`}>
                                {typeMeta.label}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${emailMeta.className}`}>
                                {emailMeta.label}
                              </span>
                            </td>
                            <td className="p-3 text-gray-600">{contact.source || 'Unknown'}</td>
                            <td className="p-3 text-xs text-gray-500">{formatDate(contact.created_at)}</td>
                          </>
                        ) : (
                          <>
                            <td className="p-3 text-sm text-gray-700">
                              <div className="font-medium">{formatResponseStatus(contact.response_status)}</div>
                              {contact.response_reason && (
                                <div className="text-xs text-gray-500">{contact.response_reason}</div>
                              )}
                            </td>
                            <td className="p-3 text-gray-600">{contact.campaign_name || 'Unknown Campaign'}</td>
                            <td className="p-3 text-xs text-gray-500">{formatDate(contact.response_date)}</td>
                          </>
                        )}
                        {config.allowDelete && (
                          <td className="p-3">
                            <button
                              onClick={() => handleDeleteContact(contact)}
                              className={`px-3 py-1 text-xs rounded hover:opacity-80 ${
                                contact.source === 'zoho'
                                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                              disabled={loading}
                            >
                              {contact.source === 'zoho' ? 'Delete from Zoho' : 'Delete'}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <button onClick={handleClose} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BouncedContactsModal;
